import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '@services/auth.service';
import { OrderService } from '@services/order.service';
import { NotificationService } from '@services/notification.service';
import { NOTIFY_CODES, preferredContact } from '@classes/job-ready';

export interface CustomerNotifyDialogData {
  order: any;
}

@Component({
  selector: 'app-customer-notify-dialog',
  template: `
    <h2 mat-dialog-title>CUSTOMER NOTIFICATION</h2>
    <mat-dialog-content class="notify-dialog">
      <p class="patient">
        {{ patientName }}
      </p>
      <p class="contact">{{ contact }}</p>

      <div class="jobs" *ngIf="jobs.length">
        <div class="jobs-title">ACTIVE JOBS</div>
        <label class="job-row" *ngFor="let job of jobs">
          <mat-checkbox
            *ngIf="waitAll"
            [checked]="isSelected(job)"
            (change)="toggleJob(job, $event.checked)"
          ></mat-checkbox>
          <span class="invoice">{{ job.invoiceNumber || '—' }}</span>
          <span class="ready" [class.not-ready]="!job.ready">
            {{ job.ready ? 'READY' : 'NOT READY' }}
          </span>
        </label>
      </div>

      <mat-checkbox [(ngModel)]="waitAll" *ngIf="jobs.length > 1">
        NOTIFY WHEN ALL ARE READY
      </mat-checkbox>

      <mat-form-field>
        <mat-label>NOTIFICATION</mat-label>
        <mat-select [(ngModel)]="code">
          <mat-option [value]="''">—</mat-option>
          <mat-option *ngFor="let option of codes" [value]="option.code">
            {{ option.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field>
        <mat-label>INITIALS</mat-label>
        <input matInput [(ngModel)]="initials" maxlength="8" autocomplete="off">
      </mat-form-field>

      <mat-form-field>
        <mat-label>NOTE</mat-label>
        <input matInput [(ngModel)]="notes" autocomplete="off">
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close()">CANCEL</button>
      <button
        mat-stroked-button
        type="button"
        [disabled]="busy || !canPickup()"
        (click)="pickup()"
      >
        PICKED UP
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="busy || !canSave()"
        (click)="save()"
      >
        SAVE
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .notify-dialog {
        min-width: 420px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        text-transform: uppercase;
      }
      .patient {
        font-weight: 700;
        margin: 0;
      }
      .contact,
      .jobs-title {
        margin: 0;
        font-size: 13px;
      }
      .job-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 4px 0;
      }
      .ready {
        font-weight: 700;
        color: #047857;
      }
      .not-ready {
        color: #b45309;
      }
      mat-dialog-actions button {
        margin-left: 8px;
      }
    `,
  ],
})
export class CustomerNotifyDialogComponent {
  codes = NOTIFY_CODES;
  waitAll = false;
  code = '';
  initials = '';
  notes = '';
  selectedIds: string[] = [];
  busy = false;
  jobs: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<CustomerNotifyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerNotifyDialogData,
    private auth: AuthService,
    private orderService: OrderService,
    private notification: NotificationService
  ) {
    this.initials = this.auth.currentUser?.initials || '';
    const summary = data.order?.linkSummary;
    this.waitAll = String(summary?.notifyPreference || data.order?.notifyPreference || '')
      .toUpperCase() === 'WAIT_ALL';
    this.jobs = summary?.jobs?.length
      ? summary.jobs
      : [
          {
            orderId: data.order?._id,
            invoiceNumber: data.order?.invoiceNumber,
            ready: true,
            patientId: data.order?.patient?._id,
          },
        ];
    const grouped = (data.order?.notifyGroupIds || []).map((id) => String(id));
    this.selectedIds = grouped.length
      ? grouped
      : this.jobs.map((job) => String(job.orderId));
  }

  get patientName(): string {
    const patient = this.data.order?.patient;
    return patient ? `${patient.lastName}, ${patient.firstName}` : '';
  }

  get contact(): string {
    return preferredContact(this.data.order?.patient);
  }

  isSelected(job: any): boolean {
    return this.selectedIds.indexOf(String(job.orderId)) !== -1;
  }

  toggleJob(job: any, checked: boolean): void {
    const id = String(job.orderId);
    if (checked) {
      this.selectedIds = Array.from(new Set([...this.selectedIds, id]));
      return;
    }
    this.selectedIds = this.selectedIds.filter((value) => value !== id);
  }

  canSave(): boolean {
    if (!String(this.initials || '').trim()) {
      return false;
    }
    const allReady =
      !this.waitAll ||
      this.jobs.filter((job) => this.isSelected(job)).every((job) => job.ready);
    if (this.waitAll && this.selectedIds.length > 1 && !allReady) {
      return true;
    }
    return !!this.code;
  }

  canPickup(): boolean {
    return !!String(this.initials || '').trim();
  }

  private targetIds(): string[] {
    if (this.waitAll && this.selectedIds.length) {
      return this.selectedIds;
    }
    return [String(this.data.order?._id)];
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }
    this.busy = true;
        const allReady =
          !this.waitAll ||
          this.jobs.filter((job) => this.isSelected(job)).every((job) => job.ready);
        this.orderService
      .notifyCustomer({
        orderIds: this.targetIds(),
        code: allReady ? this.code : '',
        initials: this.initials.trim(),
        notes: this.notes,
        waitAll: this.waitAll && this.selectedIds.length > 1,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.notification.show('success', 'Notification saved');
          this.dialogRef.close({ saved: true });
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not save the notification'
          );
        },
      });
  }

  pickup(): void {
    if (!this.canPickup()) {
      return;
    }
    this.busy = true;
    this.orderService
      .markPickedUp({
        orderId: String(this.data.order?._id),
        initials: this.initials.trim(),
        notes: this.notes,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.notification.show('success', 'Picked up');
          this.dialogRef.close({ saved: true, pickedUp: true });
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not record pickup'
          );
        },
      });
  }
}
